// AddProblemDialog — Task #223 (Leejoe 2026-06-28).
//
// REPLACES the inline-edit-mode in icdProblemBlot.js for adding NEW
// problems. The inline flow (Quill embed with an <input> child inside a
// contenteditable=false div inside a contenteditable=true editor) has had
// a recurring 1-letter-at-a-time bug across Tasks #188, #E. Root cause:
// Quill's selection observer + format module fight the inline input no
// matter what we do (we already disable Quill, stopPropagation, blur, etc.
// — each fix patches one symptom and the next code path regresses).
//
// This component sidesteps the war entirely. The clinician types into a
// regular <input> in a regular modal — zero Quill interaction during
// editing. When they hit Add, we insert a fully-formed blot via
// insertIcdProblem(). One round-trip, no character-level fight.
//
// Future: extend to "edit existing problem" mode (click an existing blot
// → dialog opens with current values pre-filled, Save replaces the blot).

import React, { useEffect, useRef, useState } from 'react';
import { searchIcd10 } from './icd10CommonProblems';
import { getBackendURL } from '../../../components/env';

const LIVE_ICD_LIMIT = 8;
const liveCache = new Map();

async function fetchLiveIcd10(query) {
  const q = String(query || '').trim();
  if (!q || q.length < 2) return [];
  if (liveCache.has(q)) return liveCache.get(q);
  try {
    const url = `${getBackendURL()}/api/icd10/search/?q=${encodeURIComponent(q)}&limit=${LIVE_ICD_LIMIT}`;
    const res = await fetch(url, { method: 'GET' });
    if (!res.ok) return [];
    const data = await res.json();
    const rows = Array.isArray(data?.results) ? data.results : [];
    const mapped = rows.map((r) => ({
      code: r.code,
      name: r.short_desc || r.long_desc || r.code,
      _source: 'live',
    }));
    liveCache.set(q, mapped);
    return mapped;
  } catch (_) {
    return [];
  }
}

function mergeResults(staticHits, liveHits, limit) {
  const seen = new Set();
  const out = [];
  for (const m of [...staticHits, ...liveHits]) {
    if (m && m.code && !seen.has(m.code)) {
      seen.add(m.code);
      out.push(m);
      if (out.length >= limit) return out;
    }
  }
  return out;
}

export default function AddProblemDialog({ isOpen, onClose, onAdd, initialName = '', initialCode = '' }) {
  const [query, setQuery] = useState('');
  const [staticResults, setStaticResults] = useState([]);
  const [liveResults, setLiveResults] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [customName, setCustomName] = useState(initialName);
  const [customCode, setCustomCode] = useState(initialCode);
  const [committed, setCommitted] = useState(false);
  const reqIdRef = useRef(0);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    // Task #229 — edit mode pre-fills the search input with the current
    // diagnosis name so the clinician sees what they're editing AND can
    // tweak directly. Create mode (no initials) opens to a blank input
    // as before. Select the text so the first keystroke replaces it
    // (matches the typical edit-an-existing-value pattern).
    setQuery(initialName || '');
    setStaticResults([]);
    setLiveResults([]);
    setSelectedIdx(0);
    setCustomName(initialName);
    setCustomCode(initialCode);
    setCommitted(false);
    const t = setTimeout(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      if (initialName) {
        try { el.select(); } catch (_) {}
      }
    }, 16);
    return () => clearTimeout(t);
  }, [isOpen, initialName, initialCode]);

  useEffect(() => {
    if (!isOpen) return;
    const hits = searchIcd10(query || '', LIVE_ICD_LIMIT);
    setStaticResults(hits);
    setSelectedIdx(0);
    const reqId = ++reqIdRef.current;
    fetchLiveIcd10(query).then((live) => {
      if (reqId !== reqIdRef.current) return;
      setLiveResults(live);
    });
  }, [query, isOpen]);

  const merged = mergeResults(staticResults, liveResults, LIVE_ICD_LIMIT);
  const activePick = merged[selectedIdx];

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx((i) => Math.min(merged.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose?.();
    }
  };

  const commit = () => {
    if (committed) return;
    // Prefer the selected suggestion; fall back to custom-typed values.
    let name = customName.trim();
    let code = customCode.trim();
    if (activePick && !name) name = activePick.name || '';
    if (activePick && !code) code = activePick.code || '';
    if (!name && !query.trim()) return; // nothing to add
    if (!name) name = query.trim();
    setCommitted(true);
    onAdd?.({ displayName: name, icd10Code: code });
    onClose?.();
  };

  if (!isOpen) return null;
  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center bg-slate-900/40 backdrop-blur-sm pt-[12vh]"
      onClick={() => onClose?.()}
      role="presentation"
    >
      <div
        className="w-[min(560px,92vw)] bg-white dark:bg-[#0e0e15] rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800/60 flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={initialName ? 'Edit ICD-10 problem' : 'Add ICD-10 problem'}
      >
        <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-800/60 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">{initialName ? 'Edit problem' : 'Add problem'}</h3>
          <button
            type="button"
            aria-label="Close"
            onClick={() => onClose?.()}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
          >✕</button>
        </div>
        <div className="p-5 flex flex-col gap-3">
          <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
            Search diagnosis (name or ICD-10 code)
          </label>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setCustomName(e.target.value); }}
            onKeyDown={onKeyDown}
            placeholder="e.g. obstructive sleep apnea, asthma, J45.40, ..."
            className="w-full text-sm px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            autoComplete="off"
            spellCheck="false"
          />
          {merged.length > 0 && (
            <ul className="max-h-[220px] overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700">
              {merged.map((m, idx) => (
                <li
                  key={`${m.code}-${idx}`}
                  role="option"
                  aria-selected={idx === selectedIdx}
                  onClick={() => {
                    // Click SELECTS the row (highlights it + fills the
                    // search/code fields). The user then clicks the green
                    // "Add to note" button to commit. Auto-commit on click
                    // was too easy to mis-fire (Leejoe accidentally added
                    // HTN by clicking the suggestion row). onMouseEnter was
                    // removed earlier so the prior "mouse moves erase the
                    // selection" race is gone — set-only is safe now.
                    setSelectedIdx(idx);
                    setCustomName(m.name);
                    setCustomCode(m.code);
                  }}
                  onDoubleClick={() => {
                    // Power-user shortcut: double-click commits.
                    if (committed) return;
                    setCommitted(true);
                    onAdd?.({ displayName: m.name, icd10Code: m.code });
                    onClose?.();
                  }}
                  className={[
                    'flex items-center justify-between gap-2 px-3 py-1.5 cursor-pointer text-sm',
                    idx === selectedIdx
                      ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-800 dark:text-emerald-200'
                      : 'hover:bg-slate-50 dark:hover:bg-slate-800/40 text-slate-700 dark:text-slate-200',
                  ].join(' ')}
                >
                  <span className="truncate">{m.name}</span>
                  <span className="font-mono text-[11px] uppercase tracking-wide bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 px-1.5 py-0.5 rounded shrink-0">
                    {m.code}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {merged.length === 0 && query.trim() && (
            <p className="text-xs text-slate-500 italic">
              No catalog match — your typed text will be added as a free-form problem (no ICD-10 code).
            </p>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-800/60 flex items-center justify-end gap-2 bg-slate-50 dark:bg-slate-900/50">
          <button
            type="button"
            onClick={() => onClose?.()}
            className="text-sm font-medium px-3 py-1.5 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={commit}
            disabled={!query.trim() && !customName.trim()}
            className="text-sm font-semibold px-3 py-1.5 rounded-lg text-white shadow-sm bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {initialName ? 'Save' : 'Add to note'}
          </button>
        </div>
      </div>
    </div>
  );
}
